import { useRouter } from "next/router";
import { useRecoilState } from "recoil";

import { EvalIds } from "graphql/generated";
import { URL_CHECKOUT } from "src/constants/url.constants";
import { sectionStateHistory } from "src/states/orderByCondition";
import { SectionStateType, SectionType } from "src/types/orderByCondition";
import { checkIsConsultOnly } from "src/utils";

export const useOrderByCondition = (
  conditionId?: EvalIds // only need conditionId when use getFirstSectionState. Most cases is go to next section
) => {
  const router = useRouter();
  const [sectionHistory, setSectionHistory] = useRecoilState(sectionStateHistory);

  /**
   * Get the very first step after complete the eval
   * @param parSkipEval flag skip eval
   * @param parSkipIntro flag skip intro
   * @returns the first state
   */
  const getFirstSectionState = (parSkipEval: boolean, parSkipIntro: boolean): SectionStateType => {
    // set default state is medication steps
    let varResult: SectionStateType = SectionType.medsSelection;

    if (parSkipEval === true) {
      varResult = SectionType.DOBInput;
    } else if (parSkipIntro === true || checkIsConsultOnly(conditionId, null)) {
      varResult = SectionType.productSelection;
    }

    // set first history as eval, the second is the landing state after complete the eval
    setSectionHistory([SectionType.evaluationQuestions, varResult]);

    return varResult;
  };

  /**
   * Get the next section state base on the current state
   * @param parCurrentState current state
   * @param parIsLastProductGroup flag if the product is the last one
   * @returns next section state
   */
  const getNextSectionState = (
    parCurrentState: SectionStateType,
    parIsLastProductGroup: boolean
  ): SectionStateType => {
    let varResult: SectionStateType = parCurrentState;

    switch (parCurrentState) {
      case SectionType.DOBInput:
        if (checkIsConsultOnly(conditionId, null)) {
          varResult = SectionType.productSelection;
          break;
        }

        varResult = SectionType.medsSelection;
        break;

      case SectionType.medsSelection:
        varResult = SectionType.productSelection;
        break;

      case SectionType.productSelection:
        varResult = SectionType.treatmentSummary;
        break;

      case SectionType.treatmentSummary:
        if (!parIsLastProductGroup) {
          varResult = SectionType.productSelection;
          break;
        }

        router.push(URL_CHECKOUT);
        break;

      default:
        break;
    }

    setSectionHistory([...sectionHistory, varResult]);

    return varResult;
  };

  const getPreSectionState = (parCurrentState: SectionStateType): SectionStateType => {
    let varResult: SectionStateType = parCurrentState;
    varResult = sectionHistory[sectionHistory.length - 2];
    setSectionHistory(sectionHistory.slice(0, sectionHistory.length - 1));
    return varResult;
  };

  const goToNextSection = (curSection: SectionStateType, isLastProductGroup = false) => {
    const nextSection = getNextSectionState(curSection, isLastProductGroup);
    goToSection(nextSection);
  };

  const goToSection = (section: SectionStateType) => {
    router.query.section = section;
    router.push(router, undefined, { shallow: true });
  };

  return {
    getFirstSectionState,
    getNextSectionState,
    getPreSectionState,
    goToNextSection,
    goToSection
  };
};
